import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { CommentDto, CommentsBodyDto } from '@app/data/dtos/comment/comment-dto';
import { mapComment, mapCommentBody } from '@app/data/mappers/comment/comment.mapper';
import { CommentRepository } from '@app/domain/repositories/comment.repository.contract';
import { Comment, CommentsPaginated } from '@app/entities/models/comment.model';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';
import { ENV } from '@environments/environment';
import { SECTION_KEY } from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MTranslateService } from '@mercadona/core/translate';
import { MRepository } from '@mercadona/core/utils/repository';

@Injectable({
  providedIn: 'root'
})
export class CommentRepositoryImpl extends MRepository implements CommentRepository {
  private readonly defaultCommentsPageSize = 10;

  constructor(private translate: MTranslateService) {
    super(`${ENV.API_URL}`);
  }

  getComments(content_id: string, pageRequest: number, last_viewed_id?: number): Observable<CommentsPaginated> {
    const params = {
      content_id,
      page: pageRequest,
      size: this.defaultCommentsPageSize,
      last_viewed_id
    };
    Object.keys(params).forEach(
      (key) => params[key as keyof typeof params] === undefined && delete params[key as keyof typeof params]
    );

    const options: MSafeAny = {
      observe: 'response',
      params: { ...params },
      context: new HttpContext().set(SECTION_KEY, 'comments')
    };
    return this.get<CommentDto[]>('/comment', options).pipe(
      map((response: MSafeAny) => ({
        comments: response.body.map((comment: CommentDto) => mapComment(comment)),
        commentsCount: parseInt(response.headers.get('x-pagination-count'), 10),
        commentsByPage: parseInt(response.headers.get('x-pagination-limit'), 10),
        totalPages: this.setMaxPages(response)
      }))
    );
  }

  getCommentsHighlighted(content_id: string): Observable<Comment[]> {
    const params = {
      content_id
    };

    const options: MSafeAny = {
      observe: 'response',
      params: { ...params },
      context: new HttpContext().set(SECTION_KEY, 'comments')
    };
    return this.get<CommentsBodyDto>('/comment/highlighted', options).pipe(map(mapCommentBody));
  }

  getCommentById(comment_id: number): Observable<Comment> {
    return this.get<CommentDto>(`/comment/${comment_id}`).pipe(map(mapComment));
  }

  createComment(
    content_id: number,
    comment: string,
    translation_key: string,
    parent_comment_id?: number,
    representedUser?: string
  ): Observable<Comment> {
    const payload: MSafeAny = { content_id, comment, translation_key, language: this.translate.getActiveLang() };

    if (parent_comment_id) {
      payload.parent_id = parent_comment_id;
    }
    if (representedUser) {
      payload.as_user = representedUser;
    }

    const request = {
      body: payload
    };

    return this.post<CommentDto>('/comment', request).pipe(map(mapComment));
  }

  deleteComment(comment_id: number): Observable<object> {
    return this.delete(`/comment/${comment_id}`);
  }

  updateComment(comment_id: number, comment: string): Observable<Comment> {
    const request = {
      body: { comment }
    };
    return this.put<CommentDto>(`/comment/${comment_id}`, request).pipe(map(mapComment));
  }

  private setMaxPages(response: MSafeAny): number {
    const count = parseInt(response.headers.get('x-pagination-count'), 10);
    const limit = parseInt(response.headers.get('x-pagination-limit'), 10);
    const total = count / limit;
    return Math.ceil(total);
  }
}
